import React, { useEffect, useRef, useState } from "react";

import ShareIcon from "../../../assets/icons/share.svg";
import useContestSettings from "../../../hooks/useContestSettings";

import { StyledWrapper } from "./actionBar.style";

const ActionBar = ({ sticky, offset }) => {
  const {
    data,
    formObject,
    formValid,
    getFormObject,
    setFormDisabled,
    submitForm,
    handleReserveSpot,
  } = useContestSettings();

  const componentRef = useRef();
  const intervalRef = useRef();

  const [fixed, setFixed] = useState(false);

  const [buttonAction, setButtonAction] = useState(data.ctaButton[0].action);
  const [buttonEnabled, setButtonEnabled] = useState(
    data.ctaButton[0].action != null,
  );
  const [buttonText, setButtonText] = useState(data.ctaButton[0].text);

  const [timerDeadline, setTimerDeadline] = useState(null);
  const [timerLabel, setTimerLabel] = useState(data.timer.label);
  const [timerStatus, setTimerStatus] = useState(data.timer.status);
  const [timerValue, setTimerValue] = useState(data.timer.value);

  useEffect(() => {
    if (!sticky) return;
    const scrollEventListener = () =>
      setFixed(componentRef.current.offsetTop - window.scrollY < offset);
    scrollEventListener();
    document.addEventListener("scroll", scrollEventListener);
    return () => document.removeEventListener("scroll", scrollEventListener);
  }, [sticky, offset]);

  useEffect(() => {
    if (!timerDeadline) return;

    setTimerValue(
      formatTimeDuration(Math.floor((timerDeadline - Date.now()) / 1000)),
    );
    const interval = setInterval(() => {
      setTimerValue(
        formatTimeDuration(Math.floor((timerDeadline - Date.now()) / 1000)),
      );
    }, 200);
    intervalRef.current = interval;

    return () => clearInterval(intervalRef.current);
  }, [timerDeadline]);

  useEffect(() => {
    if (formObject?.draftingTimeUp) {
      setFormDisabled(true);
      clearInterval(intervalRef.current);
    } else if (formObject?.draftingDeadline) {
      setTimer(formObject.draftingDeadline);
    } else if (data.reservedSpotStopTimestamp) {
      setTimer(data.reservedSpotStopTimestamp);
    }
  }, [
    data.reservedSpotStopTimestamp,
    formObject?.draftingDeadline,
    formObject?.draftingTimeUp,
  ]);

  useEffect(() => {
    if (timerLabel === "Time to submit") ctaButtonClick();
  }, []);

  const shareClick = () => {
    if (data.shareUrl) {
      navigator.clipboard.writeText(data.shareUrl);
      alert("The link to this challenge has been copied to your clipboard.");
    }
  };

  const ctaButtonClick = () => {
    setButtonEnabled(false);

    if (buttonAction === "getSolutionForm") {
      if (
        !data.solvingTimeLimit ||
        timerLabel === "Time to submit" ||
        confirm(
          `Once you start, you will have ${Math.floor(data.solvingTimeLimit / 60)} minutes to complete. Please read the Instructions before attempting. Do you want to proceed?`,
        )
      ) {
        getFormObject();
        setButtonAction("submitSolution");
        setButtonText("Submit Solution");
      }

      setButtonEnabled(true);
    }

    if (buttonAction === "redirectLogin") {
      return (window.location = data.loginRedirectPath);
    }

    if (buttonAction === "reserveSpot") {
      if (
        !data.capSubmitTimeLimitInSeconds ||
        confirm(
          `Once you reserve your spot, you will have ${Math.floor(data.capSubmitTimeLimitInSeconds / 3600)} hours to submit your idea. If you do not submit within this timeframe, you will lose your current spot in this challenge.`,
        )
      ) {
        return reserveSpot();
      }

      setButtonEnabled(true);
    }

    if (buttonAction === "showChallenges") {
      return (window.location = data.challengesPath);
    }

    if (buttonAction === "submitSolution") {
      return submitForm();
    }

    if (buttonAction == "viewSolution") {
      return (window.location = data.viewSolutionPath);
    }
  };

  const reserveSpot = () => {
    const onSuccess = (data) => {
      setButtonAction(data.ctaButton.action);
      setButtonEnabled(data.ctaButton.action != null);
      setButtonText(data.ctaButton.text);
      alert("You have successfully reserved your spot for this challenge.");
    };
    const onFailure = () =>
      alert(
        "We were unable to reserve your spot for this challenge at this time.",
      );
    handleReserveSpot(onSuccess, onFailure);
  };

  const setTimer = (deadline) => {
    setTimerLabel("Time to submit");
    setTimerDeadline(new Date(deadline));
  };

  const formatTimeDuration = (duration) => {
    if (duration <= 0) {
      clearInterval(intervalRef.current);
      getFormObject();
      setTimerStatus("expired");
      return "00:00:00";
    }

    let breaks = [3600, 60];
    let timerData = [];

    breaks.forEach((value) => {
      if (duration >= value) {
        timerData.push(
          `${Math.floor(duration / value)
            .toString()
            .padStart(2, "0")}`,
        );
        duration %= value;
      } else {
        timerData.push("00");
      }
    });
    timerData.push(`${duration.toString().padStart(2, "0")}`);

    return timerData.join(":");
  };

  return (
    <StyledWrapper ref={componentRef}>
      <div className={`row m-0 ${fixed ? "fixed-top" : ""}`}>
        <div className="contest-stats col-12 col-md-6 px-0">
          <div className="contest-stats-items d-flex justify-content-evenly justify-content-md-start my-2">
            <div className="contest-stats-item px-3 px-md-4">
              <div className={`contest-stats-timer ${timerStatus}`}>
                <div className="contest-stats-item-label">{timerLabel}</div>
                <div className="contest-stats-item-value mt-2">
                  {timerValue}
                </div>
              </div>
            </div>
            <div className="contest-stats-divider"></div>
            <div className="contest-stats-item px-3 px-md-4">
              <div className="contest-stats-bounty">
                <div className="contest-stats-item-label">Prize pot</div>
                <div className="contest-stats-item-value mt-2">
                  {data.prizePot}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="contest-actions col-12 col-md-6">
          <div className="contest-actions-items d-flex justify-content-center justify-content-md-end my-2">
            <div className="contest-actions-item">
              <div className="contest-actions-share">
                <button className="btn p-0" onClick={shareClick}>
                  Share
                  <img src={ShareIcon} />
                </button>
              </div>
            </div>
            <div className="contest-actions-item pe-md-4">
              <div className="contest-actions-cta ms-4">
                <button
                  className={`btn btn-primary rounded px-3 py-2${(formObject ? formValid : buttonEnabled) ? "" : " disabled"}`}
                  onClick={ctaButtonClick}
                >
                  {buttonText}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </StyledWrapper>
  );
};

export default ActionBar;
